import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { activitiesAPI, tasksAPI, timeTrackingAPI, usersAPI } from '../utils/api';
import { Badge } from '../components/layout/UI';
import TimeTracker from '../components/TimeTracker';
import { format, parseISO, isValid } from 'date-fns';
import DateWarnings, { useDateWarnings } from '../components/DateWarnings';

import { useToast, apiError } from '../components/Toast';
import CustomFieldsEditor from '../components/CustomFieldsEditor';
import { useEditableRecord, SaveBar } from '../components/UnsavedChanges';
import { useMaster } from '../hooks/useMasters';
import { getPermLevel, isAdminRole } from '../utils/rolesConfig';
import { useAuth } from '../hooks/useAuth';
import OccurrenceCalendar from '../components/OccurrenceCalendar';
import HoursInput, { formatHM } from '../components/HoursInput';
import { ArrowLeft } from 'lucide-react';
import { STATUS_OPTIONS } from '../utils/workStatus';


const fmt = (v, p = 'MMM d, yyyy') => {
  if (!v) return '—';
  try { const d = typeof v === 'string' ? parseISO(v) : new Date(v); return isValid(d) ? format(d, p) : '—'; }
  catch { return '—'; }
};

const InfoRow = ({ label, value, children }) =>
  (value || children) ? (
    <div className="flex gap-3 py-2.5 border-b" style={{ borderColor:'var(--border)' }}>
      <p className="text-xs font-semibold w-28 flex-shrink-0 pt-0.5 uppercase tracking-wider"
        style={{ color:'var(--text-secondary)' }}>{label}</p>
      <div className="flex-1 text-sm" style={{ color:'var(--text)' }}>{children || value}</div>
    </div>
  ) : null;

const ACTIVITY_STATUS_FALLBACK = STATUS_OPTIONS;
const PRIORITY_FALLBACK = ['Low','Medium','High','Critical']
  .map(v => ({ value:v, label:v }));

// Matches activities_recurrence_check (phase65).
const RECURRENCE = [
  { v:'none',     l:'Does not repeat' },
  { v:'daily',    l:'Every day' },
  { v:'weekdays', l:'Every weekday (Mon–Fri)' },
  { v:'weekly',   l:'Every week, on the same weekday' },
];
const WEEKDAY_OF = (d) => d
  ? new Date(String(d).slice(0,10) + 'T00:00:00').toLocaleDateString('en-GB', { weekday:'long' })
  : '';

export default function ActivityDetailPage() {
  const toast = useToast();
  const dateWarn = useDateWarnings();
  const { id }   = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [task,     setTask]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  // One form, no edit mode. See components/UnsavedChanges.jsx.

  const { user } = useAuth();
  const { form, set, discard, dirtyFields, patch } = useEditableRecord(activity);

  /**
   * Seeing and changing are different things. Anyone who can reach this page
   * keeps the whole view; only the creator, the assignee or an admin may
   * change anything.
   */
  const canEdit = !activity || isAdminRole(user?.role) || getPermLevel(user?.role) === 'Manager'
    || activity.created_by === user?.id
    || activity.assigned_to === user?.id;

  /**
   * Recording a day is narrower than editing the activity. PUT
   * /workplan/occurrence allows the owner or an Admin level, and not a
   * Manager, so this must ask the same question or a Manager would get a
   * clickable calendar that is refused on every click.
   */
  const canRecordDays = !!activity && (isAdminRole(user?.role)
    || activity.created_by === user?.id
    || activity.assigned_to === user?.id);

  // Admin-controlled, with the DB's own values as the fallback. My first pass
  // at this page hardcoded both lists, which is the thing Masters exists to stop.
  const STATUS_OPTS   = useMaster('activity_status', ACTIVITY_STATUS_FALLBACK);
  const PRIORITY_OPTS = useMaster('priority',        PRIORITY_FALLBACK);

  // The Assigned To field needs the list; this page never loaded one, because
  // until now it showed the assignee as read-only text.
  const [users, setUsers] = useState([]);
  useEffect(() => {
    let cancelled = false;
    usersAPI.getAll()
      .then(r => { if (!cancelled) setUsers(r.data || []); })
      .catch(() => { if (!cancelled) setUsers([]); });
    return () => { cancelled = true; };
  }, []);
  const [saving,   setSaving]   = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [timeLogs, setTimeLogs] = useState([]);
  const [logsLoad, setLogsLoad] = useState(false);

  // Loaded only when the tab is opened — most visits never look at it.
  useEffect(() => {
    if (activeTab !== 'timelogs' || !id) return;
    let cancelled = false;
    setLogsLoad(true);
    timeTrackingAPI.getLogs(id)
      .then(r => { if (!cancelled) setTimeLogs(Array.isArray(r.data) ? r.data : (r.data?.logs || [])); })
      .catch(e => { if (!cancelled) { setTimeLogs([]); toast.error(apiError(e, 'Time log load failed:')); } })
      .finally(() => { if (!cancelled) setLogsLoad(false); });
    return () => { cancelled = true; };
  }, [activeTab, id]);

  useEffect(() => {
    setLoading(true);
    activitiesAPI.getById(id)
      .then(async r => {
        setActivity(r.data);
        if (r.data.task_id) {
          try {
            const t = await tasksAPI.getById(r.data.task_id);
            setTask(t.data);
          } catch(e) { /* task may not be accessible */ }
        }
      })
      .catch(e => toast.error(apiError(e, 'Could not load this page.')))
      .finally(() => setLoading(false));
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await activitiesAPI.update(id, patch());
      toast.success('Activity updated');
      setActivity(r.data);
      // Allowed, but the activity may now sit outside its task's window.
      dateWarn.show(r.data);
    } catch(e) { toast.error(apiError(e, 'Failed to save. Check your permissions.')); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/>
    </div>
  );

  if (!activity) return (
    <div className="card text-center py-16">
      <p style={{ color:'var(--text)' }}>Activity not found</p>
      <button onClick={() => navigate(-1)} className="btn-secondary mt-4">Go back</button>
    </div>
  );

  return (
    <div className="space-y-5">
      <DateWarnings {...dateWarn} />
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm" style={{ color:'var(--text-secondary)' }}>
        <button onClick={() => navigate('/activities')}
          className="flex items-center gap-1.5 hover:text-brand-500 transition-colors">
          <ArrowLeft size={16}/> Activities
        </button>
        {task && (
          <>
            <span>/</span>
            <Link to={`/tasks/${task.id}`} className="hover:text-brand-500 transition-colors">
              {task.title}
            </Link>
          </>
        )}
      </div>

      {/* Header */}
      <div className="card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <Badge value={activity.status}   type="status"/>
              <Badge value={activity.priority} type="priority"/>
            </div>
            <h1 className="text-xl font-bold mb-1" style={{ color:'var(--text)' }}>{activity.title}</h1>
            {task && (
              <p className="text-sm" style={{ color:'var(--text-secondary)' }}>
                Under task: <Link to={`/tasks/${task.id}`} className="text-brand-500 hover:underline">{task.title}</Link>
                {task.project_name && <> · <span>{task.project_name}</span></>}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* There is no edit mode now, so the timer is simply available
                until the activity is finished. */}
            {activity.status !== 'Completed' && (
              <TimeTracker
                activityId={activity.id}
                isRunning={!!activity.active_timer_started_at}
                startedAt={activity.active_timer_started_at}
              />
            )}

          </div>
        </div>

        {/* Progress bar if hours set */}
        {(activity.estimated_hours || activity.actual_hours) && (
          <div className="mt-4">
            <div className="flex justify-between text-xs mb-1" style={{ color:'var(--text-secondary)' }}>
              <span>Hours</span>
              <span>{parseFloat(activity.actual_hours||0).toFixed(1)} / {parseFloat(activity.estimated_hours||0).toFixed(1)}h estimated</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background:'var(--border)' }}>
              <div className="h-full rounded-full bg-brand-500" style={{
                width: activity.estimated_hours
                  ? `${Math.min(100, Math.round((parseFloat(activity.actual_hours||0)/parseFloat(activity.estimated_hours))*100))}%`
                  : '0%'
              }}/>
            </div>
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div className="border-b flex gap-1" style={{ borderColor:'var(--border)' }}>
        {[
          { key:'details',   label:'Details'    },
          { key:'timelogs',  label:'Time Logs'  },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors
              ${activeTab === tab.key ? 'border-brand-500 text-brand-500' : 'border-transparent'}`}
            style={activeTab !== tab.key ? { color:'var(--text-secondary)' } : {}}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Details */}
      {activeTab === 'details' && <div className="card space-y-5">
        {/* One fieldset rather than a disabled prop on every input: it
            cannot be half-applied, and a field added later is covered
            without anyone remembering. */}
        <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0 }}>
        <p className="text-xs font-bold uppercase tracking-wider" style={{ color:'#2E539F' }}>Details</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><label className="label">Title *</label>
            <input className="input" value={form.title||''} onChange={e=>set({title:e.target.value})}/></div>
          <div className="md:col-span-2"><label className="label">Description</label>
            <textarea className="input" rows={3} value={form.description||''} onChange={e=>set({description:e.target.value})}/></div>
          <div><label className="label">Status</label>
            <select className="select" value={form.status||''} onChange={e=>set({status:e.target.value})}>
              {STATUS_OPTS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select></div>
          <div><label className="label">Priority</label>
            <select className="select" value={form.priority||''} onChange={e=>set({priority:e.target.value})}>
              {PRIORITY_OPTS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select></div>
          <div><label className="label">Assigned to</label>
            <select className="select" value={form.assigned_to||''} onChange={e=>set({assigned_to:e.target.value})}>
              <option value="">Unassigned</option>
              {users.filter(u=>u.is_active).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
              The API accepted this all along; this page showed it read-only.
            </p></div>
          <div><label className="label">Estimated hours</label>
            <HoursInput value={form.estimated_hours}
              onChange={v => set({ estimated_hours: v })} /></div>
          <div><label className="label">Manual hours</label>
            <HoursInput value={form.manual_hours}
              onChange={v => set({ manual_hours: v })} />
            {/* The sum is shown as it is typed, so the total is never a
                surprise after saving. logged_hours is the saved figure; the
                manual part is whatever is in the box right now. */}
            <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
              {formatHM(form.manual_hours)} entered
              {' + '}{formatHM(activity.logged_hours)} logged by the timer
              {' = '}
              <strong style={{ color:'var(--text)' }}>
                {formatHM((Number(form.manual_hours) || 0) + (Number(activity.logged_hours) || 0))}
              </strong>
              {' '}actual
            </p></div>
          <div><label className="label">Available from</label>
            <input type="date" className="input" value={(form.start_date||'').slice(0,10)} onChange={e=>set({start_date:e.target.value})}/></div>
          <div><label className="label">Due by</label>
            <input type="date" className="input" value={(form.due_date||'').slice(0,10)} onChange={e=>set({due_date:e.target.value})}/>
            <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
              The window the work must fall in. Use the Workplan to pick the day.
            </p></div>
          {/* A standing duty is ONE activity that runs for a long time, done
              over and over. It stays open; each day is ticked on its own in
              the To-Do list, which never closes the activity. */}
          <div><label className="label">Repeats</label>
            <select className="select" value={form.recurrence || 'none'}
              onChange={e=>set({ recurrence: e.target.value,
                                 ...(e.target.value === 'none' ? { recurrence_until: null } : {}) })}>
              {RECURRENCE.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
            {(form.recurrence && form.recurrence !== 'none') && (
              <p className="text-xs mt-1" style={{ color: form.start_date ? 'var(--text-secondary)' : '#ef4444' }}>
                {!form.start_date
                  ? 'Set "Available from" first — it is the date the repeat starts from.'
                  : form.recurrence === 'weekly'
                    ? `Every ${WEEKDAY_OF(form.start_date)}, from ${String(form.start_date).slice(0,10)}.`
                    : `From ${String(form.start_date).slice(0,10)}. Ticking a day never completes the activity.`}
              </p>
            )}
          </div>
          {(form.recurrence && form.recurrence !== 'none') && (
            <div><label className="label">Repeat until</label>
              <input type="date" className="input"
                value={(form.recurrence_until||'').slice(0,10)}
                min={(form.start_date||'').slice(0,10) || undefined}
                onChange={e=>set({recurrence_until:e.target.value})}/>
              <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
                Leave empty for a duty with no end date.
              </p></div>
          )}
        </div>
        <CustomFieldsEditor appliesTo="activity"
          values={form.custom_fields || {}}
          onChange={v => set({ custom_fields: v })} />
        <div className="pt-3 border-t grid grid-cols-1 md:grid-cols-3 gap-4" style={{ borderColor:'var(--border)' }}>
          {[['Actual hours', `${formatHM(activity.actual_hours)}  (${formatHM(activity.manual_hours)} entered + ${formatHM(activity.logged_hours)} logged)`],
            ['Created', fmt(activity.created_at, 'MMM d, yyyy HH:mm')],
            ['Updated', fmt(activity.updated_at, 'MMM d, yyyy HH:mm')]].map(([l,v]) => (
            <div key={l}>
              <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color:'var(--text-secondary)' }}>{l}</p>
              <p className="text-sm" style={{ color:'var(--text)' }}>{v}</p>
            </div>
          ))}
        </div>
      </fieldset>
        {!canEdit && (
          <p className="text-xs" style={{ color:'var(--text-secondary)' }}>
            Read-only: activities can be changed by the person who created them,
            the person they are assigned to, or an administrator.
          </p>
        )}
      </div>}

      {canEdit && <SaveBar dirtyFields={dirtyFields} discard={discard} onSave={handleSave} saving={saving} />}

      {/* Time Logs */}
      {activeTab === 'timelogs' && (
        <div className="card overflow-hidden p-0">
          <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
            <h3 className="font-semibold text-sm" style={{ color:'var(--text)' }}>Time Logs</h3>
            {timeLogs.length > 0 && (
              <span className="text-xs font-medium" style={{ color:'var(--text-secondary)' }}>
                Total: <strong style={{ color:'var(--text)' }}>
                  {Math.floor(timeLogs.reduce((s,l)=>s+(l.duration_minutes||0),0)/60)}h{' '}
                  {timeLogs.reduce((s,l)=>s+(l.duration_minutes||0),0)%60}m
                </strong>
              </span>
            )}
          </div>
          {logsLoad ? (
            <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/></div>
          ) : timeLogs.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <p className="text-2xl mb-2">⏱️</p>
              <p className="text-sm font-medium" style={{ color:'var(--text)' }}>No time logged yet</p>
              <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>Use the timer button to start tracking time on this activity.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b" style={{ borderColor:'var(--border)' }}>
                    {['Date','Started','Ended','Duration','Logged By','Notes'].map(h => (
                      <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider"
                        style={{ color:'var(--text-secondary)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {timeLogs.map((log, i) => (
                    <tr key={log.id} className="border-b last:border-0 hover:bg-[var(--surface-alt)] transition-colors"
                      style={{ borderColor:'var(--border)', background: i%2===1?'rgba(0,0,0,.015)':'transparent' }}>
                      <td className="px-4 py-2.5 text-xs" style={{ color:'var(--text-secondary)' }}>
                        {log.start_time ? new Date(log.start_time).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-xs" style={{ color:'var(--text-secondary)' }}>
                        {log.start_time ? new Date(log.start_time).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}) : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-xs" style={{ color:'var(--text-secondary)' }}>
                        {log.end_time ? new Date(log.end_time).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}) : <span style={{ color:'#10b981' }}>Running…</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                          style={{ background:'rgba(46,83,159,.1)', color:'#2E539F' }}>
                          {log.duration_minutes ? `${Math.floor(log.duration_minutes/60)}h ${log.duration_minutes%60}m` : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs" style={{ color:'var(--text-secondary)' }}>
                        {log.user_name || '—'}
                      </td>
                      <td className="px-4 py-2.5 text-xs" style={{ color:'var(--text-secondary)' }}>
                        {log.notes || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Only renders for a repeating activity; it returns null otherwise. */}
      {activity?.recurrence && activity.recurrence !== 'none' && (
        <div className="mt-5">
          <OccurrenceCalendar activityId={activity.id} canEdit={canRecordDays} />
        </div>
      )}
    </div>
  );
}
